import { useCallback, useEffect, useMemo, useState } from "react";
import { DateInput } from "@/components/DateInput";
import { BarChart, Bar, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isTruckingBusiness } from "@/lib/industries";
import { Truck, MapPinLine, GasPump, DownloadSimple, CircleNotch, Plus, ArrowSquareOut, LinkBreak, RoadHorizon, TrendUp } from "@phosphor-icons/react";

const emptyForm = {
  date: new Date().toISOString().slice(0, 10),
  start_location: "",
  end_location: "",
  miles: "",
  vehicle: "",
  load_number: "",
  purpose: "",
};

const money = (value) => `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const currentQuarter = () => {
  const now = new Date();
  return `${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`;
};

export function Mileage({ businessId, business }) {
  const trucking = isTruckingBusiness(business);
  const [data, setData] = useState(null);
  const [fuelRows, setFuelRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quarter, setQuarter] = useState(currentQuarter());
  const [form, setForm] = useState(emptyForm);
  const [fuelDrafts, setFuelDrafts] = useState({});

  const load = useCallback(async (selectedQuarter = quarter) => {
    setLoading(true);
    try {
      const [mileageRes, fuelRes, iftaRes] = await Promise.all([
        api.get(`/businesses/${businessId}/mileage`),
        trucking ? api.get(`/businesses/${businessId}/ifta/fuel-expenses`, { params: { quarter: selectedQuarter } }) : Promise.resolve({ data: { items: [] } }),
        trucking ? api.get(`/businesses/${businessId}/ifta/report`, { params: { quarter: selectedQuarter } }) : Promise.resolve({ data: null }),
      ]);
      setData({ ...mileageRes.data, ifta_dashboard: iftaRes.data || mileageRes.data.ifta_dashboard || null });
      setFuelRows(fuelRes.data.items || []);
      setFuelDrafts(Object.fromEntries((fuelRes.data.items || []).map((row) => [row.id, { gallons: row.gallons || "", state: row.state || "", trip_id: row.trip_id || "" }])));
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Couldn't load mileage right now.");
    } finally {
      setLoading(false);
    }
  }, [businessId, quarter, trucking]);

  useEffect(() => {
    if (!businessId) return;
    load(quarter);
  }, [businessId, quarter, load]);

  useEffect(() => {
    if (!trucking) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("action") === "log-trip") {
      const el = document.getElementById("ifta-trip-form");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [trucking]);

  const trips = useMemo(() => data?.trips || [], [data]);
  const dashboard = data?.ifta_dashboard || null;
  const mapsConfigured = data?.maps_configured !== false;
  const tripOptions = useMemo(() => trips.filter((trip) => (trip.quarter_key || quarter) === quarter).slice(0, 12), [trips, quarter]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = trucking
        ? form
        : { ...form, miles: Number(form.miles || 0) };
      await api.post(`/businesses/${businessId}/mileage`, payload);
      toast.success(trucking ? "Trip logged and route split by state." : "Trip logged.");
      setForm({ ...emptyForm, date: form.date });
      await load(quarter);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Couldn't save that trip.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (tripId) => {
    try {
      await api.delete(`/businesses/${businessId}/mileage/${tripId}`);
      toast.success("Trip deleted.");
      await load(quarter);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Couldn't delete that trip.");
    }
  };

  const saveFuelLink = async (row) => {
    const draft = fuelDrafts[row.id] || {};
    try {
      await api.post(`/businesses/${businessId}/ifta/fuel-expenses/link`, {
        transaction_id: row.id,
        trip_id: draft.trip_id || "",
        gallons: Number(draft.gallons || 0),
        state: draft.state || "",
      });
      toast.success("Fuel purchase linked for IFTA.");
      await load(quarter);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Couldn't link that fuel purchase.");
    }
  };

  if (loading && !data) {
    return <div className="bk-card p-6 flex items-center gap-3" data-testid="mileage-loading"><CircleNotch size={18} className="animate-spin" color="var(--bk-accent)" /> <span style={{ color: "var(--bk-text-2)" }}>Loading mileage…</span></div>;
  }

  return (
    <div className="space-y-6" data-testid="mileage-dashboard">
      <div className="bk-rowan-card p-6" data-testid="mileage-hero-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>{trucking ? "Google Maps IFTA tracker" : "Mileage tracker"}</p>
            <h3 className="font-display text-3xl font-bold mt-2" style={{ color: "var(--bk-text)" }}>{trucking ? "Log the route, let Rowan do the state math." : "Track every deductible mile."}</h3>
            <p className="text-sm mt-3 max-w-3xl" style={{ color: "var(--bk-text-2)" }} data-testid="mileage-rowan-copy">
              {trucking
                ? (dashboard?.rowan_message || "Montana to Texas is a long haul. I've got every mile logged by state.")
                : "Every trip here turns into mileage deduction history without the glove-box notebook drama."}
            </p>
          </div>
          {trucking && (
            <div className="flex flex-wrap gap-2">
              <a href={`/api/businesses/${businessId}/ifta/report.csv?quarter=${quarter}`} target="_blank" rel="noreferrer" data-testid="ifta-export-csv" className="px-4 py-2 rounded-full text-sm font-semibold border inline-flex items-center gap-2" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                <DownloadSimple size={16} weight="bold" /> CSV
              </a>
              <a href={`/api/businesses/${businessId}/ifta/report.pdf?quarter=${quarter}`} target="_blank" rel="noreferrer" data-testid="ifta-export-pdf" className="bk-btn-primary px-4 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2">
                <DownloadSimple size={16} weight="bold" /> PDF / print
              </a>
            </div>
          )}
        </div>
      </div>

      {trucking && dashboard && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium" style={{ color: "var(--bk-text)" }}>Quarter</label>
            <select value={quarter} onChange={(e) => setQuarter(e.target.value)} data-testid="ifta-quarter-select" className="rounded-xl border px-3 py-2 text-sm bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
              {[currentQuarter(), `${new Date().getFullYear()}-Q1`, `${new Date().getFullYear()}-Q2`, `${new Date().getFullYear()}-Q3`, `${new Date().getFullYear()}-Q4`, `${new Date().getFullYear()-1}-Q4`].filter((value, index, array) => array.indexOf(value) === index).map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ background: "rgba(240,230,211,0.8)", color: "var(--bk-accent)" }} data-testid="ifta-due-chip">
              Due {dashboard.due_date} · {dashboard.due_in_days} days
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="ifta-summary-metrics">
            {[
              { label: "Total miles this quarter", value: `${Number(dashboard.total_miles || 0).toLocaleString()} mi`, icon: Truck },
              { label: "Fuel efficiency", value: dashboard.fuel_efficiency_mpg ? `${dashboard.fuel_efficiency_mpg} mpg` : "Add gallons", icon: TrendUp },
              { label: "Cost per mile", value: dashboard.cost_per_mile ? money(dashboard.cost_per_mile) : "Need fuel links", icon: GasPump },
              { label: "States driven", value: String(dashboard.states_count || 0), icon: MapPinLine },
            ].map((card) => (
              <div key={card.label} className="bk-card p-5" data-testid={`ifta-metric-${card.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-accent)" }}>
                  <card.icon size={16} weight="duotone" /> {card.label}
                </div>
                <p className="font-display text-3xl font-bold mt-3" style={{ color: "var(--bk-text)" }}>{card.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="bk-card p-5" data-testid="ifta-state-chart-card">
              <div className="flex items-center gap-2 mb-4">
                <RoadHorizon size={18} weight="duotone" color="var(--bk-accent)" />
                <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Miles by state</h4>
              </div>
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dashboard.miles_by_state || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(26,39,68,0.08)" />
                    <XAxis dataKey="state" tick={{ fontSize: 12, fill: "#475569" }} />
                    <YAxis tick={{ fontSize: 12, fill: "#475569" }} />
                    <Tooltip formatter={(value) => [`${value} miles`, "Miles"]} />
                    <Bar dataKey="miles" fill="#8B4A2A" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-5">
              <div className="bk-card p-5" data-testid="ifta-routes-card">
                <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Most common routes</h4>
                <div className="space-y-3 mt-4">
                  {(dashboard.common_routes || []).map((route) => (
                    <div key={route.route} className="flex items-center justify-between gap-3 text-sm">
                      <div>
                        <p style={{ color: "var(--bk-text)" }}>{route.route}</p>
                        <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>{route.count} trip{route.count === 1 ? "" : "s"}</p>
                      </div>
                      <span className="font-semibold" style={{ color: "var(--bk-text)" }}>{route.miles.toFixed(1)} mi</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bk-card p-5" data-testid="ifta-fuel-review-card">
                <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Fuel purchase review</h4>
                <p className="text-sm mt-2" style={{ color: "var(--bk-text-2)" }}>
                  {dashboard.fuel_review?.linked_count || 0} linked · {dashboard.fuel_review?.review_count || 0} still need gallons/state before filing.
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      <form id="ifta-trip-form" onSubmit={submit} className="bk-card p-6 space-y-4" data-testid="mileage-form">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{trucking ? "Log Trip" : "Add trip"}</h4>
            <p className="text-sm mt-1" style={{ color: "var(--bk-text-2)" }}>{trucking ? "Enter the haul details and Rowan will calculate the route plus per-state mileage automatically." : "Track date, route, purpose, and miles for your deduction log."}</p>
          </div>
          <button type="submit" disabled={saving} data-testid="mileage-submit" className="bk-btn-primary px-5 py-3 rounded-full text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <><CircleNotch size={16} className="animate-spin" /> Saving…</> : <><Plus size={16} weight="bold" /> {trucking ? "Log trip" : "Add miles"}</>}
          </button>
        </div>

        <div className={`grid gap-4 ${trucking ? "md:grid-cols-2 xl:grid-cols-3" : "md:grid-cols-2 xl:grid-cols-4"}`}>
          <label className="space-y-1 text-sm">
            <span style={{ color: "var(--bk-text)" }}>Date</span>
            <DateInput value={form.date} onChange={(e) => setForm((current) => ({ ...current, date: e.target.value }))} data-testid="mileage-date" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
          <label className="space-y-1 text-sm">
            <span style={{ color: "var(--bk-text)" }}>{trucking ? "Origin" : "Start location"}</span>
            <input value={form.start_location} onChange={(e) => setForm((current) => ({ ...current, start_location: e.target.value }))} data-testid="mileage-start" placeholder="Billings, MT" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
          <label className="space-y-1 text-sm">
            <span style={{ color: "var(--bk-text)" }}>{trucking ? "Destination" : "End location"}</span>
            <input value={form.end_location} onChange={(e) => setForm((current) => ({ ...current, end_location: e.target.value }))} data-testid="mileage-end" placeholder="Denver, CO" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
          {trucking ? (
            <>
              <label className="space-y-1 text-sm">
                <span style={{ color: "var(--bk-text)" }}>Truck / vehicle</span>
                <input value={form.vehicle} onChange={(e) => setForm((current) => ({ ...current, vehicle: e.target.value }))} data-testid="mileage-vehicle" placeholder="Truck 17" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </label>
              <label className="space-y-1 text-sm">
                <span style={{ color: "var(--bk-text)" }}>Load number (optional)</span>
                <input value={form.load_number} onChange={(e) => setForm((current) => ({ ...current, load_number: e.target.value }))} data-testid="mileage-load-number" placeholder="LD-2049" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
              </label>
              {!mapsConfigured && (
                <label className="space-y-1 text-sm">
                  <span style={{ color: "var(--bk-text)" }}>Manual miles</span>
                  <input type="number" min="0" step="0.1" value={form.miles} onChange={(e) => setForm((current) => ({ ...current, miles: e.target.value }))} data-testid="mileage-manual-miles" placeholder="645" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                </label>
              )}
            </>
          ) : (
            <label className="space-y-1 text-sm">
              <span style={{ color: "var(--bk-text)" }}>Miles</span>
              <input type="number" min="0" step="0.1" value={form.miles} onChange={(e) => setForm((current) => ({ ...current, miles: e.target.value }))} data-testid="mileage-miles" placeholder="42" className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
            </label>
          )}
          <label className={`space-y-1 text-sm ${trucking ? "xl:col-span-3" : "xl:col-span-2"}`}>
            <span style={{ color: "var(--bk-text)" }}>Purpose</span>
            <input value={form.purpose} onChange={(e) => setForm((current) => ({ ...current, purpose: e.target.value }))} data-testid="mileage-purpose" placeholder={trucking ? "Bozeman to Denver produce haul" : "Client visit"} className="w-full px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
          </label>
        </div>
        {trucking && !mapsConfigured && (
          <div className="rounded-[20px] border p-4" style={{ borderColor: "#E7B6A8", background: "#FFF7F2" }} data-testid="maps-key-missing-warning">
            <p className="text-sm" style={{ color: "#8B4A2A" }}>Google Maps auto-routing is not configured in this preview yet, so Rowan can still track manual miles — but state-by-state routing needs the `GOOGLE_MAPS_API_KEY` environment variable.</p>
          </div>
        )}
      </form>

      {trucking && (
        <div className="bk-card p-6" data-testid="ifta-fuel-linker">
          <div className="flex items-center gap-2 mb-4">
            <GasPump size={18} weight="duotone" color="var(--bk-accent)" />
            <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>Fuel purchases for IFTA</h4>
          </div>
          <p className="text-sm mb-4" style={{ color: "var(--bk-text-2)" }}>Review gallons and state for each fuel expense from Books. Rowan keeps the receipt in the books — you just teach the tax math where the fuel actually happened.</p>
          <div className="space-y-4" data-testid="ifta-fuel-list">
            {fuelRows.length === 0 && (
              <div className="rounded-[20px] border p-4" style={{ borderColor: "var(--bk-border)", background: "#FFFCF6" }} data-testid="ifta-fuel-empty-state">
                <p className="text-sm" style={{ color: "var(--bk-text-2)" }}>No fuel purchases were detected from Books for this quarter yet.</p>
              </div>
            )}
            {fuelRows.map((row) => {
              const draft = fuelDrafts[row.id] || { gallons: "", state: "", trip_id: "" };
              return (
                <div key={row.id} className="rounded-[22px] border p-4 bg-white" style={{ borderColor: row.linked ? "rgba(22,163,74,0.22)" : "var(--bk-border)" }} data-testid={`ifta-fuel-row-${row.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{row.description}</p>
                      <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{row.date} · {row.category}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{money(row.amount)}</p>
                      <p className="text-xs mt-1" style={{ color: row.linked ? "var(--bk-success)" : "#B45309" }}>{row.linked ? "Linked" : "Needs review"}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[120px_160px_1fr_140px] gap-3 mt-4">
                    <input type="number" min="0" step="0.01" value={draft.gallons} onChange={(e) => setFuelDrafts((current) => ({ ...current, [row.id]: { ...draft, gallons: e.target.value } }))} data-testid={`ifta-fuel-gallons-${row.id}`} placeholder="Gallons" className="px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                    <input value={draft.state} onChange={(e) => setFuelDrafts((current) => ({ ...current, [row.id]: { ...draft, state: e.target.value } }))} data-testid={`ifta-fuel-state-${row.id}`} placeholder="State" className="px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} />
                    <select value={draft.trip_id} onChange={(e) => setFuelDrafts((current) => ({ ...current, [row.id]: { ...draft, trip_id: e.target.value } }))} data-testid={`ifta-fuel-trip-${row.id}`} className="px-3 py-2.5 rounded-xl border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}>
                      <option value="">Not tied to one trip</option>
                      {tripOptions.map((trip) => <option key={trip.id} value={trip.id}>{trip.date} · {trip.start_location} → {trip.end_location}</option>)}
                    </select>
                    <button type="button" onClick={() => saveFuelLink(row)} data-testid={`ifta-fuel-save-${row.id}`} className="bk-btn-primary px-4 py-2.5 rounded-xl text-sm font-semibold">Save fuel link</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bk-card p-6" data-testid="mileage-trips-card">
        <h4 className="font-display text-xl font-bold" style={{ color: "var(--bk-text)" }}>{trucking ? "Recent trips" : "Trip log"}</h4>
        <div className="space-y-4 mt-4">
          {trips.map((trip) => (
            <div key={trip.id} className="rounded-[22px] border p-4 bg-white" style={{ borderColor: "var(--bk-border)" }} data-testid={`mileage-trip-${trip.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold" style={{ color: "var(--bk-text)" }}>{trip.start_location || "Start"} → {trip.end_location || "End"}</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{trip.date} {trip.vehicle ? `· ${trip.vehicle}` : ""} {trip.load_number ? `· Load ${trip.load_number}` : ""}</p>
                  {trip.purpose && <p className="text-sm mt-2" style={{ color: "var(--bk-text)" }}>{trip.purpose}</p>}
                  {trip.route_summary && <p className="text-xs mt-2" style={{ color: "var(--bk-accent)" }} data-testid={`mileage-route-summary-${trip.id}`}>{trip.route_summary}</p>}
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl" style={{ color: "var(--bk-text)" }}>{Number(trip.miles || 0).toLocaleString()} mi</p>
                  <p className="text-xs mt-1" style={{ color: "var(--bk-text-2)" }}>{money(trip.deduction || 0)} deduction value</p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button type="button" onClick={() => remove(trip.id)} data-testid={`delete-mileage-${trip.id}`} className="px-3 py-2 rounded-full text-xs font-semibold border" style={{ borderColor: "var(--bk-border)", color: "#B23B1F" }}>
                  <LinkBreak size={14} weight="bold" className="inline mr-1" /> Delete trip
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}